import React, { useState } from "react";
import { OptionCard } from "./OptionCard";

// Color picker. With options → swatch grid (using OptionCard so styles
// like "icon" are honored). With config.enable_hex_input → also renders
// a free hex input below the grid; typed hexes are appended to the
// answer alongside selected option IDs.
//
// Storage shape:
//   single-select & no hex     → optionId (number)
//   multi or hex                → array mixing IDs and "#RRGGBB" strings
const HEX_RE = /^#?[0-9a-fA-F]{3,8}$/;

export const ColorField = ({ question, value, onChange, lang }) => {
  const allowMultiple = !!question.allow_multiple;
  const enableHex = !!(question.config && question.config.enable_hex_input);
  const opts = question.options || [];
  const qStyle = question.config && question.config.option_style;
  const [hex, setHex] = useState("");
  const [hexErr, setHexErr] = useState("");

  // Always normalize to a list internally for selection bookkeeping; we
  // unwrap to scalar at write time when allow_multiple is false.
  const list =
    Array.isArray(value) ? value : value === null || value === undefined ? [] : [value];

  const setList = (next) => {
    if (allowMultiple || enableHex) onChange(next);
    else onChange(next[0] ?? null);
  };

  const isSel = (id) => list.includes(id);
  const toggle = (id) => {
    if (allowMultiple || enableHex) {
      setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
    } else {
      setList([id]);
    }
  };

  const submitHex = () => {
    const trimmed = hex.trim();
    if (!trimmed) return;
    if (!HEX_RE.test(trimmed)) {
      setHexErr(lang === "ar" ? "رمز سداسي غير صالح" : "Invalid hex code.");
      return;
    }
    const normalized = trimmed.startsWith("#") ? trimmed : "#" + trimmed;
    if (!list.includes(normalized)) setList([...list, normalized]);
    setHex("");
    setHexErr("");
  };
  const removeHex = (h) => setList(list.filter((x) => x !== h));

  // Native color picker fallback when there are no options AND no hex input.
  if (!opts.length && !enableHex) {
    return (
      <input
        type="color"
        value={typeof value === "string" ? value : "#000000"}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  const useGrid = !qStyle || (qStyle !== "compact" && qStyle !== "icon");

  return (
    <div>
      {opts.length ? (
        <div
          className={useGrid ? "dq-options" : ""}
          style={
            useGrid
              ? undefined
              : { display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }
          }
        >
          {opts.map((opt) => {
            // For options that lack an image, fall back to a solid-colour
            // swatch using the value field (or metadata.hex).
            const swatch =
              opt.image
                ? opt
                : {
                    ...opt,
                    image: undefined,
                    metadata: { ...(opt.metadata || {}), style: opt.metadata?.style || "icon" },
                  };
            const swatchColor = opt.value || (opt.metadata && opt.metadata.hex) || "#ccc";
            return (
              <div key={opt.id} style={{ position: "relative" }}>
                {!opt.image && (
                  <span
                    aria-hidden
                    style={{
                      position: "absolute",
                      top: 12,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 30,
                      height: 30,
                      background: swatchColor,
                      borderRadius: "50%",
                      pointerEvents: "none",
                      border: "1px solid #ddd",
                    }}
                  />
                )}
                <OptionCard
                  option={swatch}
                  selected={isSel(opt.id)}
                  onClick={() => toggle(opt.id)}
                  lang={lang}
                  questionStyle={qStyle}
                />
              </div>
            );
          })}
        </div>
      ) : null}

      {enableHex ? (
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 13,
              textAlign: "center",
              marginBottom: 8,
              color: "#444",
            }}
          >
            {lang === "ar"
              ? "أو أدخل رمزًا سداسيًا"
              : "OR enter the hex code of colours you want."}
          </div>
          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              justifyContent: "center",
              maxWidth: 320,
              margin: "0 auto",
            }}
          >
            <input
              className="dq-input"
              placeholder="#"
              value={hex}
              onChange={(e) => {
                setHex(e.target.value);
                setHexErr("");
              }}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), submitHex())}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              onClick={submitHex}
              style={{
                padding: "8px 14px",
                border: "1px solid #000",
                background: "#000",
                color: "#fff",
                borderRadius: 4,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              +
            </button>
          </div>
          {hexErr ? <div className="dq-error">{hexErr}</div> : null}
          {/* Show the typed hexes as removable chips */}
          {list.filter((x) => typeof x === "string" && x.startsWith("#")).length ? (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginTop: 10,
                justifyContent: "center",
              }}
            >
              {list
                .filter((x) => typeof x === "string" && x.startsWith("#"))
                .map((h) => (
                  <span
                    key={h}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "3px 8px",
                      border: "1px solid #ddd",
                      borderRadius: 999,
                      fontSize: 12,
                    }}
                  >
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        background: h,
                        border: "1px solid #999",
                      }}
                    />
                    {h}
                    <button
                      type="button"
                      onClick={() => removeHex(h)}
                      aria-label="remove"
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        color: "#888",
                        fontSize: 16,
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
